/**
 * Display helpers.
 *
 * The one rule they all follow: **null is not zero**. A unit that has never
 * reported has no output power; rendering that as "0 W" claims a measurement
 * that was never taken, and on a fleet page it makes a silent unit look like an
 * idle one. Every formatter here returns an em dash for null and the callers
 * style it as absent data.
 */

export const DASH = '—';

export function num(
  value: number | null | undefined,
  digits = 0,
  suffix = '',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return value.toFixed(digits) + suffix;
}

/** Watts, shown in kW once the number stops being readable in W. */
export function watts(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  if (Math.abs(value) >= 10_000) return `${(value / 1000).toFixed(2)} kW`;
  return `${Math.round(value)} W`;
}

export function kwh(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return DASH;
  return value.toFixed(digits);
}

export function pct(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return DASH;
  return `${value.toFixed(digits)}%`;
}

export function bytesMb(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} GB`;
  return `${value} MB`;
}

/** Coarse relative time. Deliberately coarse: to the minute is precision this
 *  data does not have, and "3 days ago" is the decision-relevant granularity. */
export function since(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return DASH;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return DASH;
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? '1 day ago' : `${d} days ago`;
}

export function stamp(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleString(undefined, {
    year: '2-digit',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return DASH;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

/**
 * The unit's operational name.
 *
 * `internal_id` first, because that is the hostname and what people say. Falling
 * back to `label` — the enclosure sticker — only when there is no hostname, and
 * marked as such, because a unit with no internal_id is precisely a unit nobody
 * has provisioned. The sticker is display only and is explicitly not an
 * identifier: two units could carry the same one by mistake.
 */
export function unitName(u: {
  internal_id: string | null;
  label: string | null;
  unit_id: number;
}): string {
  return u.internal_id ?? u.label ?? `unit ${u.unit_id}`;
}

/** URL segment for a unit. The API accepts internal_id or the numeric id. */
export function unitKey(u: { internal_id: string | null; unit_id: number }): string {
  return u.internal_id ?? String(u.unit_id);
}
